import React, { useState, useEffect } from 'react';
import Icon from '../../../../components/AppIcon';
import { productService } from '../../../../services/productService';

const KitCompositionTab = ({ productData }) => {
  const [kitComponents, setKitComponents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (productData?.id && productData?.productType === 'kit') {
      loadKitComponents();
    } else {
      setLoading(false);
    }
  }, [productData?.id]);

  const loadKitComponents = async () => {
    try {
      setLoading(true);
      const kitWithComponents = await productService?.getKitWithComponents(productData?.id);
      setKitComponents(kitWithComponents?.components || []);
    } catch (error) {
      console.error('Error loading kit components:', error);
      setKitComponents([]);
    } finally {
      setLoading(false);
    }
  };

  if (productData?.productType !== 'kit') {
    return (
      <div className="text-center py-16">
        <Icon name="Package" size={56} className="text-muted-foreground mx-auto mb-4" />
        <p className="text-lg text-muted-foreground">Este produto não é um kit</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Icon name="Loader" size={40} className="animate-spin text-primary" />
      </div>
    );
  }

  if (kitComponents?.length === 0) {
    return (
      <div className="text-center py-16">
        <Icon name="AlertCircle" size={56} className="text-muted-foreground mx-auto mb-4" />
        <p className="text-lg text-muted-foreground">Nenhum produto encontrado neste kit</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* SKU Summary Section */}
      <div className="bg-gradient-to-r from-blue-50 to-purple-50 border-2 border-blue-300 rounded-xl p-5">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg flex items-center justify-center">
            <Icon name="Hash" size={20} className="text-white" />
          </div>
          <div>
            <h3 className="text-lg font-bold text-gray-900">SKUs dos Produtos</h3>
            <p className="text-sm text-gray-600">Códigos originais de cada item do kit</p>
          </div>
        </div>
        <div className="bg-white rounded-lg p-4 space-y-2">
          {kitComponents?.map((component, index) => (
            <div key={component?.id || index} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
              <div className="flex items-center gap-3">
                <span className="w-6 h-6 bg-blue-100 text-blue-700 rounded-full flex items-center justify-center text-xs font-bold">
                  {index + 1}
                </span>
                <div>
                  <p className="font-semibold text-gray-900">{component?.sku}</p>
                  <p className="text-xs text-gray-500">{component?.name}</p>
                </div>
              </div>
              <span className="text-sm text-gray-600">Qtd: {component?.quantity}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
        <div className="flex items-center gap-2 mb-2">
          <Icon name="Package" size={20} className="text-green-600 dark:text-green-400" />
          <h3 className="text-lg font-semibold text-foreground">Produtos que compõem o kit</h3>
        </div>
        <p className="text-sm text-muted-foreground">
          Este kit contém {kitComponents?.length} produto{kitComponents?.length > 1 ? 's' : ''}
        </p>
      </div>

      <div className="space-y-4">
        {kitComponents?.map((component, index) => (
          <div key={component?.id || index} className="bg-card border border-border rounded-lg p-6">
            <div className="flex items-start gap-4 mb-4">
              {component?.images?.[0] && (
                <img
                  src={component?.images?.[0]}
                  alt={component?.name}
                  className="w-20 h-20 object-cover rounded-lg border border-border"
                  onError={(e) => {
                    e.currentTarget.src = '/assets/images/no_image.png';
                  }}
                />
              )}
              <div className="flex-1">
                <h4 className="text-lg font-semibold text-foreground mb-1">{component?.name}</h4>
                <p className="text-sm text-muted-foreground">SKU: {component?.sku}</p>
                <p className="text-sm text-muted-foreground">Quantidade: {component?.quantity}</p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Pricing Information */}
              <div className="space-y-2">
                <h5 className="text-sm font-semibold text-foreground flex items-center gap-2">
                  <Icon name="DollarSign" size={16} />
                  Preços
                </h5>
                <div className="bg-muted/50 rounded-lg p-3 space-y-1">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Custo:</span>
                    <span className="font-medium text-foreground">R$ {component?.cost?.toFixed(2) || '0.00'}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Preço:</span>
                    <span className="font-medium text-primary">R$ {component?.price?.toFixed(2) || '0.00'}</span>
                  </div>
                  {component?.stock !== undefined && (
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Estoque:</span>
                      <span className="font-medium text-foreground">{component?.stock}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Classification */}
              <div className="space-y-2">
                <h5 className="text-sm font-semibold text-foreground flex items-center gap-2">
                  <Icon name="Tag" size={16} />
                  Classificação
                </h5>
                <div className="bg-muted/50 rounded-lg p-3 space-y-1">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Marca:</span>
                    <span className="font-medium text-foreground">{component?.brand || 'N/A'}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Categoria:</span>
                    <span className="font-medium text-foreground">{component?.category || 'N/A'}</span>
                  </div>
                  {component?.condition && (
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Condição:</span>
                      <span className="font-medium text-foreground capitalize">{component?.condition}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Physical Attributes */}
              <div className="space-y-2">
                <h5 className="text-sm font-semibold text-foreground flex items-center gap-2">
                  <Icon name="Box" size={16} />
                  Dimensões
                </h5>
                <div className="bg-muted/50 rounded-lg p-3 space-y-1">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Peso:</span>
                    <span className="font-medium text-foreground">{component?.weight || '0'} kg</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Dimensões:</span>
                    <span className="font-medium text-foreground">
                      {component?.width || '0'} x {component?.height || '0'} x {component?.length || '0'} cm
                    </span>
                  </div>
                </div>
              </div>

              {/* Fiscal Data */}
              <div className="space-y-2">
                <h5 className="text-sm font-semibold text-foreground flex items-center gap-2">
                  <Icon name="FileCheck" size={16} />
                  Dados Fiscais
                </h5>
                <div className="bg-muted/50 rounded-lg p-3 space-y-1">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">EAN:</span>
                    <span className="font-medium text-foreground">{component?.ean || 'N/A'}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">NCM:</span>
                    <span className="font-medium text-foreground">{component?.ncm || 'N/A'}</span>
                  </div>
                  {component?.cest && (
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">CEST:</span>
                      <span className="font-medium text-foreground">{component?.cest}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Supplier Information */}
              {(component?.supplierName || component?.supplierState) && (
                <div className="space-y-2 md:col-span-2">
                  <h5 className="text-sm font-semibold text-foreground flex items-center gap-2">
                    <Icon name="Truck" size={16} />
                    Fornecedor
                  </h5>
                  <div className="bg-muted/50 rounded-lg p-3 space-y-1">
                    {component?.supplierName && (
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">Nome:</span>
                        <span className="font-medium text-foreground">{component?.supplierName}</span>
                      </div>
                    )}
                    {component?.supplierState && (
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">Estado:</span>
                        <span className="font-medium text-foreground">{component?.supplierState}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* CSV Original Data */}
              {component?.csvOriginalData && Object.keys(component?.csvOriginalData)?.length > 0 && (
                <div className="space-y-2 md:col-span-2">
                  <h5 className="text-sm font-semibold text-foreground flex items-center gap-2">
                    <Icon name="Database" size={16} />
                    Dados do CSV Original
                  </h5>
                  <div className="bg-muted/50 rounded-lg p-3 space-y-1">
                    {Object.entries(component?.csvOriginalData)?.map(([key, value]) => {
                      if (!value || key === 'id') return null;
                      return (
                        <div key={key} className="flex justify-between text-sm">
                          <span className="text-muted-foreground capitalize">{key?.replace(/_/g, ' ')}:</span>
                          <span className="font-medium text-foreground">{String(value)}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Images */}
              {component?.images?.length > 0 && (
                <div className="space-y-2 md:col-span-2">
                  <h5 className="text-sm font-semibold text-foreground flex items-center gap-2">
                    <Icon name="Image" size={16} />
                    Imagens ({component?.images?.length})
                  </h5>
                  <div className="flex gap-2 overflow-x-auto pb-2">
                    {component?.images?.map((img, imgIndex) => (
                      <img
                        key={imgIndex}
                        src={img}
                        alt={`${component?.name} - ${imgIndex + 1}`}
                        className="w-16 h-16 object-cover rounded border border-border flex-shrink-0"
                        onError={(e) => {
                          e.currentTarget.src = '/assets/images/no_image.png';
                        }}
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default KitCompositionTab;